import { useCustomerData } from "@contexts/CustomerContext";
import { useParkingLot } from "@contexts/ParkingLotContext";
import { useSlideContext } from "@contexts/SlideContext";
import type { SlideDefinition } from "@data/types";
import { useFullscreen } from "@hooks/useFullscreen";
import { ActionIcon, Badge, Box, Group, Text, Tooltip } from "@repo/ui/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconMaximize,
  IconMinimize,
  IconParking,
} from "@repo/ui/icons";
import { useMemo } from "react";

type Props = {
  onExit?: () => void;
};

type SectionRange = {
  label: string;
  color: string;
  start: number;
  end: number;
};

const SECTION_META: Record<string, { label: string; color: string }> = {
  "01": { label: "Intro", color: "var(--mantine-color-emerald-5)" },
  "02": { label: "Demo", color: "var(--mantine-color-blue-5)" },
  "03": { label: "Platform", color: "var(--mantine-color-violet-5)" },
  "04": { label: "Discussion", color: "var(--mantine-color-orange-5)" },
};

function getSectionRanges(slides: SlideDefinition[]): SectionRange[] {
  const sections: SectionRange[] = [];
  let current: SectionRange | null = null;

  for (let i = 0; i < slides.length; i++) {
    const slide = slides[i]!;
    if (slide.component === "SectionDividerSlide") {
      const num = (slide.props as { number?: string }).number ?? "";
      const meta = SECTION_META[num];
      if (meta) {
        if (current) {
          current.end = i - 1;
          sections.push(current);
        }
        current = { label: meta.label, color: meta.color, start: i, end: i };
      }
    }
  }
  if (current) {
    current.end = slides.length - 1;
    sections.push(current);
  }

  const firstSection = sections[0];
  if (firstSection && firstSection.start > 0) {
    sections.unshift({
      label: "Opening",
      color: "rgba(255,255,255,0.4)",
      start: 0,
      end: firstSection.start - 1,
    });
  }

  return sections;
}

export function SlideNav({}: Props) {
  const { slides } = useCustomerData();
  const sections = useMemo(() => getSectionRanges(slides), [slides]);
  const { currentSlide, totalSlides, next, prev, goTo, isFirst, isLast } =
    useSlideContext();
  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { items, openDrawer } = useParkingLot();

  const currentSection = sections.find(
    (s) => currentSlide >= s.start && currentSlide <= s.end,
  );

  const openCount = items.filter((i) => i.status === "open").length;

  return (
    <Box
      px="xl"
      py="sm"
      style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(0,0,0,0.4)",
        backdropFilter: "blur(12px)",
      }}
    >
      <Group justify="space-between" align="center">
        <Group gap="xs">
          {/* <Tooltip label="Exit slides (Esc)">
            <ActionIcon variant="subtle" color="gray" onClick={onExit} size="lg">
              <IconX size={18} />
            </ActionIcon>
          </Tooltip> */}
        </Group>

        <Group gap="md" align="center">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={prev}
            disabled={isFirst}
            size="lg"
          >
            <IconArrowLeft size={18} />
          </ActionIcon>

          <Group gap={3} align="center">
            {sections.map((section) => {
              const width = section.end - section.start + 1;
              const isActive =
                currentSlide >= section.start && currentSlide <= section.end;
              const progress = isActive
                ? ((currentSlide - section.start + 1) / width) * 100
                : currentSlide > section.end
                  ? 100
                  : 0;

              return (
                <Tooltip
                  key={section.label}
                  label={`${section.label} (${section.start + 1}–${section.end + 1})`}
                >
                  <Box
                    h={6}
                    style={{
                      width: `${Math.max(width * 3, 16)}px`,
                      borderRadius: 3,
                      background: "rgba(255,255,255,0.1)",
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => goTo(section.start)}
                  >
                    <Box
                      h="100%"
                      style={{
                        width: `${progress}%`,
                        background: section.color,
                        borderRadius: 3,
                        transition: "width 0.2s ease",
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Group>

          <Text size="xs" c="dimmed" miw={80} ta="center">
            {currentSection?.label} · {currentSlide + 1}/{totalSlides}
          </Text>

          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={next}
            disabled={isLast}
            size="lg"
          >
            <IconArrowRight size={18} />
          </ActionIcon>
        </Group>

        <Group gap="xs">
          <Tooltip label="Parking Lot (P)">
            <Box pos="relative" style={{ display: "inline-flex" }}>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={openDrawer}
                size="lg"
              >
                <IconParking size={18} />
              </ActionIcon>
              {openCount > 0 && (
                <Badge
                  size="xs"
                  circle
                  color="orange"
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    pointerEvents: "none",
                    minWidth: 16,
                    height: 16,
                    fontSize: 10,
                  }}
                >
                  {openCount}
                </Badge>
              )}
            </Box>
          </Tooltip>
          <Tooltip
            label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={toggleFullscreen}
              size="lg"
            >
              {isFullscreen ? (
                <IconMinimize size={18} />
              ) : (
                <IconMaximize size={18} />
              )}
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Box>
  );
}
